import { useId, useState } from 'react';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { useDoiTenThuMuc, useTaoThuMuc } from '../hooks/useTriThuc';
import type { Folder } from '../types';

const DAI_TEN = 255;

/** Tạo thư mục trong thư mục đang mở (`cha`), hoặc đổi tên `thuMuc`. */
export function FormThuMuc({
  kbId,
  cha,
  tenCha,
  thuMuc,
  open,
  onClose,
}: {
  kbId: string;
  cha: string | null;
  /** Tên thư mục cha, để người dùng biết thư mục mới nằm ở đâu; `null` là gốc bộ. */
  tenCha: string | null;
  thuMuc?: Folder;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const tao = useTaoThuMuc(kbId);
  const doiTen = useDoiTenThuMuc(kbId);
  const dangLuu = tao.isPending || doiTen.isPending;

  const [ten, setTen] = useState(thuMuc?.name ?? '');
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{ loi: unknown; truong: Record<string, string> } | null>(null);

  const loiTen = (daGui && !ten.trim() ? t('triThuc.thuMuc.loiTenTrong') : undefined) ?? loiServer?.truong['/name'];
  const loiChung = loiServer && !loiServer.truong['/name'] ? loiServer.loi : null;
  const coThayDoi = ten.trim() !== (thuMuc?.name ?? '');

  const gui = async () => {
    setDaGui(true);
    if (!ten.trim() || !coThayDoi || dangLuu) return;
    setLoiServer(null);
    try {
      if (thuMuc) await doiTen.mutateAsync({ id: thuMuc.id, name: ten.trim() });
      else await tao.mutateAsync({ name: ten.trim(), parent_id: cha });
      onClose();
    } catch (err) {
      setLoiServer({ loi: err, truong: loiTheoTruong(err) });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={thuMuc ? t('triThuc.thuMuc.doiTenTieuDe', { ten: thuMuc.name }) : t('triThuc.thuMuc.taoTieuDe')}
      width={440}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loiChung) && <LoiTrongHop>{dichLoi(loiChung)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangLuu} disabled={!coThayDoi}>
              {dangLuu ? t('chung.dangLuu') : thuMuc ? t('triThuc.thuMuc.doiTen') : t('triThuc.thuMuc.tao')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <Field
          label={t('triThuc.thuMuc.fTen')}
          required
          hint={thuMuc ? undefined : t('triThuc.thuMuc.namTrong', { noi: tenCha ?? t('triThuc.thuMuc.gocBo') })}
          error={loiTen}
        >
          {(p) => (
            <Input
              {...p}
              autoFocus
              maxLength={DAI_TEN}
              placeholder={t('triThuc.thuMuc.fTenViDu')}
              value={ten}
              onChange={(e) => {
                setTen(e.target.value);
                setLoiServer(null);
              }}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
